import {
  QueryKey,
  UseQueryOptions,
  UseMutationOptions,
  useQuery,
  useMutation,
} from "@tanstack/react-query";

// API client configuration
const API_URL = import.meta.env.VITE_API_URL;

// Track if we're currently refreshing the token to prevent multiple refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Fetch CSRF token on app start
async function fetchCsrfToken() {
  try {
    const response = await fetch(`${API_URL}/api/auth/csrf/`, {
      credentials: "include",
      mode: "cors",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Origin: window.location.origin,
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      console.error("Failed to fetch CSRF token:", response.status);
      return;
    }

    // Log response headers for debugging
    const headers: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });

    // Wait for cookie to be set
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Verify cookie was set
    const allCookies = document.cookie;

    const csrfToken = allCookies
      .split(";")
      .map((c) => c.trim())
      .find((row) => row.startsWith("csrftoken="))
      ?.split("=")[1];

    if (!csrfToken) {
      console.error("CSRF token not found in cookies after fetch");
    }

    return csrfToken;
  } catch (error) {
    console.error("Error fetching CSRF token:", error);
    return null;
  }
}

// Call it when module loads and export it
const initialCsrfPromise = fetchCsrfToken();
export { fetchCsrfToken };

type RequestConfig = {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
};

export type ApiError = {
  message: string;
  errors?: Record<string, string[]>;
  non_field_errors?: string[];
  detail?: string;
  status?: number;
};

// Helper to create query keys from URL paths
function createQueryKey(path: string): QueryKey {
  return [path];
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_URL}/api/auth/token/refresh/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!response.ok) {
      throw new Error("Failed to refresh token");
    }

    const data = await response.json();
    localStorage.setItem("accessToken", data.access);
    return data.access;
  } catch (error) {
    console.error("Error refreshing token:", error);
    return null;
  }
}

// Update the client function to handle query params
async function client<T>(
  endpoint: string,
  config: RequestConfig & { params?: Record<string, string> } = {}
): Promise<T> {
  await initialCsrfPromise;

  const executeRequest = async (token?: string | null) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...config.headers,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const csrfToken = document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrftoken="))
      ?.split("=")[1];

    if (csrfToken) {
      headers["X-CSRFToken"] = csrfToken;
    }

    // Handle query params
    const url = new URL(`${API_URL}${endpoint}`);
    if (config.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    const response = await fetch(url.toString(), {
      ...config,
      headers,
      credentials: "include",
      mode: "cors",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw {
        message: error.message || "An error occurred",
        errors: error.errors,
        non_field_errors: error.non_field_errors,
        detail: error.detail,
        status: response.status,
      };
    }

    // Return null for 204 No Content responses
    if (response.status === 204) {
      return null;
    }

    return response.json();
  };

  try {
    return await executeRequest(localStorage.getItem("accessToken"));
  } catch (error: unknown) {
    const apiError = error as ApiError;
    if ((apiError.status === 403 || apiError.status === 401) && !isRefreshing) {
      isRefreshing = true;
      try {
        refreshPromise = refreshPromise || refreshAccessToken();
        const newToken = await refreshPromise;
        if (newToken) {
          return await executeRequest(newToken);
        }
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    }
    if (apiError.status === 401 || apiError.status === 403) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      window.location.href = "/login";
    }
    throw error;
  }
}

// Update the useGet type to include params
export const api = {
  useGet: <TData = unknown, TTransformed = TData, TError = ApiError>(
    path: string,
    options?: Omit<
      UseQueryOptions<TData, TError, TTransformed>,
      "queryKey" | "queryFn"
    > & {
      params?: Record<string, string>;
    }
  ) => {
    const { params, ...queryOptions } = options || {};
    return useQuery<TData, TError, TTransformed>({
      queryKey: [...createQueryKey(path), params], // Include params in query key
      queryFn: () => client<TData>(path, { params }),
      ...queryOptions,
    });
  },

  useMutation: <TData = unknown, TVariables = unknown, TError = ApiError>(
    path: string,
    options?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">
  ) => {
    return useMutation<TData, TError, TVariables>({
      mutationFn: (variables) =>
        client<TData>(path, {
          method: "POST",
          body: JSON.stringify(variables),
        }),
      ...options,
    });
  },

  // Method-specific mutation hooks
  usePost: <TData = unknown, TVariables = unknown, TError = ApiError>(
    path: string,
    options?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">
  ) => {
    return api.useMutation<TData, TVariables, TError>(path, options);
  },

  usePut: <TData = unknown, TVariables = unknown, TError = ApiError>(
    path: string,
    options?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">
  ) => {
    return useMutation<TData, TError, TVariables>({
      mutationFn: (variables) =>
        client<TData>(path, {
          method: "PUT",
          body: JSON.stringify(variables),
        }),
      ...options,
    });
  },

  usePatch: <TData = unknown, TVariables = unknown, TError = ApiError>(
    path: string,
    options?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">
  ) => {
    return useMutation<TData, TError, TVariables>({
      mutationFn: (variables) =>
        client<TData>(path, {
          method: "PATCH",
          body: JSON.stringify(variables),
        }),
      ...options,
    });
  },

  useDelete: <TData = unknown, TError = ApiError>(
    path: string,
    options?: Omit<UseMutationOptions<TData, TError, void>, "mutationFn">
  ) => {
    return useMutation<TData, TError, void>({
      mutationFn: () =>
        client<TData>(path, {
          method: "DELETE",
        }),
      ...options,
    });
  },
};

// Error handling utility
export function handleApiError(error: unknown): ApiError {
  if (error && typeof error === "object" && "status" in error) {
    return error as ApiError;
  }
  return {
    message: "An unexpected error occurred",
  };
}

// Query key factory
export const queryKeys = {
  auth: {
    user: ["auth", "user"] as const,
    session: ["auth", "session"] as const,
  },
  // Add more query keys as needed
} as const;
